/**
 * Structural data for the full-body-MRI page's new sections (AHD-6064).
 * No display strings live here — copy is in the `tests` i18n namespace,
 * keyed by the `keyBase` fields below (resolve as `tt(`${keyBase}Title`)`).
 */

export interface MriUspStep {
  /** i18n key base in the `tests` namespace, e.g. "mriUsp1" */
  keyBase: string
  /** placeholder label, shown when `image` is not set */
  placeholderLabel: string
  /** Blob path passed to `blobMediaUrl()`. When set, replaces the placeholder. */
  image?: string
}

export const MRI_USP_STEPS: MriUspStep[] = [
  {
    keyBase: "mriUsp1",
    placeholderLabel: "Whole-body scan",
    image: "/images/mri/mri-usp1-scanner-screen-v2.jpg",
  },
  {
    keyBase: "mriUsp2",
    placeholderLabel: "European research cohorts",
    image: "/images/mri/mri-guidelines-octorad.png",
  },
  {
    keyBase: "mriUsp3",
    placeholderLabel: "AI quantification",
    image: "/images/homepage/service-mri-machine.jpeg",
  },
]

export interface MriOrgan {
  /** stable id, also used in aria + test ids */
  id: string
  /** i18n key base in the `tests` namespace, e.g. "mriOrganBrain" */
  keyBase: string
  /** hotspot centre as a percentage of the body-illustration box
   *  (x = % of width, y = % of height). Tune these to the final
   *  anatomical illustration once it is in place. */
  x: number
  y: number
}

export const MRI_ORGANS: MriOrgan[] = [
  { id: "brain", keyBase: "mriOrganBrain", x: 50, y: 11 },
  { id: "neck", keyBase: "mriOrganNeck", x: 50, y: 20 },
  { id: "chest", keyBase: "mriOrganChest", x: 50, y: 32 },
  { id: "spine", keyBase: "mriOrganSpine", x: 53, y: 40 },
  { id: "liver", keyBase: "mriOrganLiver", x: 41, y: 46 },
  { id: "spleen", keyBase: "mriOrganSpleen", x: 59, y: 47 },
  { id: "pancreas", keyBase: "mriOrganPancreas", x: 50, y: 51 },
  { id: "kidneys", keyBase: "mriOrganKidneys", x: 43, y: 56 },
  { id: "bladder", keyBase: "mriOrganBladder", x: 50, y: 64 },
  { id: "pelvis", keyBase: "mriOrganPelvis", x: 50, y: 70 },
  { id: "mens", keyBase: "mriOrganMens", x: 45, y: 74 },
  { id: "womens", keyBase: "mriOrganWomens", x: 55, y: 74 },
]

export interface MriWalkthroughStep {
  keyBase: string
  placeholderLabel: string
  /** Blob path passed to `blobMediaUrl()`. When set, replaces the placeholder. */
  image?: string
}

export const MRI_WALKTHROUGH_STEPS: MriWalkthroughStep[] = [
  {
    keyBase: "mriWalkStep1",
    placeholderLabel: "Clinic check-in",
    image: "/images/mri/walkthrough/step-checkin.png",
  },
  {
    keyBase: "mriWalkStep2",
    placeholderLabel: "The scan",
    image: "/images/mri/walkthrough/step-scan.png",
  },
  {
    keyBase: "mriWalkStep3",
    placeholderLabel: "Radiologist analysis",
    image: "/images/mri/walkthrough/step-radiologist-v2.png",
  },
  {
    keyBase: "mriWalkStep4",
    placeholderLabel: "Health report",
    image: "/images/mri/walkthrough/step-report-v2.png",
  },
  {
    keyBase: "mriWalkStep5",
    placeholderLabel: "Doctor consultation",
    image: "/images/mri/walkthrough/step-consultation-home.jpg",
  },
]

export interface MriCredibilityItem {
  keyBase: string
}

export const MRI_CREDIBILITY_ITEMS: MriCredibilityItem[] = [
  { keyBase: "mriCred1" },
  { keyBase: "mriCred2" },
  { keyBase: "mriCred3" },
  { keyBase: "mriCred4" },
  { keyBase: "mriCred5" },
]
